import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { games } from "@/data/providers";
import { slotPath } from "@/lib/format";

export function SlotsHubPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const title = isSv ? "Spelautomater 2026 — bästa slots i Sverige" : "Slots 2026 — best slots in Sweden";
  const desc = isSv
    ? "Populära spelautomater hos svenska casinon. Klassiska slots, bonusslots, jackpotts och nya releaser från NetEnt, Play'n GO, Pragmatic Play och Hacksaw Gaming."
    : "Popular slots at Swedish casinos: classic slots, bonus slots, jackpots and new releases from NetEnt, Play'n GO, Pragmatic Play and Hacksaw Gaming.";

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: title }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-6 max-w-3xl [&>h2]:mt-8 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3">
        <h2>{isSv ? "Vad är en spelautomat?" : "What is a slot?"}</h2>
        <p>
          {isSv
            ? "En spelautomat är ett digitalt casinospel där hjul snurrar och kombinationer av symboler avgör utbetalningen. Moderna slots kombinerar klassisk hjul-mekanik med bonusspel, fria snurr, scatter-symboler och multiplikatorer. Varje spel har en RTP-siffra (Return to Player) — den teoretiska andelen av insatserna som betalas tillbaka över tid."
            : "A slot is a digital casino game where reels spin and symbol combinations determine the payout. Modern slots combine classic reel mechanics with bonus games, free spins, scatter symbols and multipliers. Every game has an RTP (Return to Player) figure — the theoretical share of bets paid back over time."}
        </p>
        <h2>{isSv ? "Hur väljer du rätt slot?" : "How do you pick the right slot?"}</h2>
        <p>
          {isSv
            ? "Tre saker att titta på: RTP, volatilitet och bonusfunktioner. Hög RTP (96 % och uppåt) ger längre sessioner. Låg volatilitet betyder mindre men frekventare vinster, medan hög volatilitet ger sällsynta men stora träffar. Bonusfunktionerna avgör hur spelet känns — letar du efter en lugn slot med många små vinster eller en explosiv slot med stort bonusspel?"
            : "Three things to look at: RTP, volatility and bonus features. High RTP (96% and up) means longer sessions. Low volatility means smaller, more frequent wins, while high volatility produces rare but large hits. The bonus features decide how the game feels — are you after a calm slot with many small wins or an explosive game with a big bonus round?"}
        </p>
      </article>

      <h2 className="mt-10 text-2xl font-bold">
        {isSv ? "Populära spelautomater" : "Popular slots"}
      </h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {games
          .filter((g) => g.type === "slot" || g.type === "jackpot")
          .map((g) => (
            <Link
              key={g.id}
              to={slotPath(prefix, lang, g.slug)}
              className="card-interactive p-5 no-underline"
            >
              <h3 className="text-lg font-semibold">{g.name}</h3>
              <p className="mt-1 text-xs uppercase tracking-wide text-ink-500">
                {g.provider_slug}
              </p>
              <p className="mt-2 text-sm text-ink-700 line-clamp-3">
                {isSv ? g.description_sv : g.description_en}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {typeof g.rtp === "number" && (
                  <span className="pill-brand">RTP {g.rtp.toFixed(2)}%</span>
                )}
                {g.volatility && (
                  <span className="pill-brand">
                    {isSv ? "Volatilitet" : "Volatility"}: {g.volatility}
                  </span>
                )}
              </div>
            </Link>
          ))}
      </div>

      <InternalLinks context="slot" />
    </div>
  );
}
